import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth, GoogleUserData, UserDetails } from '../contexts/AuthContext';
import { auth, GoogleAuthProvider, signInWithCredential } from '../firebase';
import firebaseDataService from '../services/FirebaseDataService';
import salesService from '../services/salesService';
import { createCompany } from '../services/migration/companyDataService';
import { getScreenPermissionsByType } from '../services/migration/personnelDataService';
import { styled } from '@mui/material/styles';
import ReCAPTCHA from 'react-google-recaptcha';
import { BRAND, BRAND_GRADIENTS } from '../config/brand';
import {
  Box,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  Snackbar,
  Alert,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Modal,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Link
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import GoogleIcon from '@mui/icons-material/Google';
import { GOOGLE_LOGIN_ENABLED } from '../config/featureFlags';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmployeeModal from './EmployeeModal';
import KvkkModal from './KvkkModal';
import BusinessIcon from '@mui/icons-material/Business';
import { useTranslation } from 'react-i18next';
import Header from './welcome/components/Header';
import Footer from './welcome/components/Footer';
import { salesApi, authApi } from '../config/apiClient';
import { usePublicRegistrationFlow } from '../contexts/PublicRegistrationFlowContext';
import { getPanelHomePath } from './superadmin/superAdminHome';
import WhatsAppFloatingButton from './common/WhatsAppFloatingButton';

// Stil tanımlamaları
const PageContainer = styled(Box)(({ theme }) => ({
  height: '100vh',
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  background: BRAND_GRADIENTS.vertical, // BRAND'den
  position: 'relative',
  overflowY: 'scroll',
}));

const LoginContent = styled(Box)(({ theme }) => ({
  flexGrow: 1,
  width: '100%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 2),
  [theme.breakpoints.down('md')]: {
    alignItems: 'flex-start',
    padding: theme.spacing(2, 1),
  },
}));

const LoginSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  width: '100%',
  maxWidth: '1300px',
  margin: '0 auto',
  gap: theme.spacing(4),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(2),
  },
}));

const ImageContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    display: 'none',
  },
}));

const FormContainer = styled(Box)(({ theme }) => ({
  flex: 1.2,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  zIndex: 1,
  maxWidth: '450px',
  [theme.breakpoints.down('md')]: {
    maxWidth: '100%',
  },
}));

const Image = styled('img')(({ theme }) => ({
  maxWidth: '90%',
  height: 'auto',
  objectFit: 'contain',
  borderRadius: theme.spacing(2),
  boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  transition: 'transform 0.3s ease',
  '&:hover': {
    transform: 'scale(1.03)',
  },
}));

const HomeButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: theme.spacing(2),
  left: theme.spacing(2),
  color: 'white',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 0.3)',
  },
  padding: theme.spacing(1.5),
  width: 56,
  height: 56,
}));

const LoginForm = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3, 2),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  borderRadius: 16,
  maxWidth: 400,
  width: '100%',
  margin: '0 auto',
}));

const Logo = styled('img')({
  width: 230,
  maxWidth: '100%',
  height: 'auto',
  borderRadius: 10,
  marginBottom: 16,
  objectFit: 'contain',
});

const StyledTextField = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
  },
}));

const LoginButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1.2),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  width: '100%',
  backgroundColor: '#0D47A1',
  '&:hover': {
    backgroundColor: '#0A3C87',
  },
}));

const GoogleButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(1.2),
  borderRadius: 12,
  fontWeight: 'bold',
  fontSize: 16,
  width: '100%',
  backgroundColor: '#ffffff',
  color: '#757575',
  border: '1px solid #dddddd',
  '&:hover': {
    backgroundColor: '#f5f5f5',
    borderColor: '#cccccc',
  },
  textTransform: 'none',
  boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
}));

const StoreButton = styled(Button)(({ theme }) => ({
  margin: theme.spacing(0.5),
  padding: theme.spacing(0.8),
  borderRadius: 12,
  fontSize: 13,
  width: '45%',
  textTransform: 'none',
}));

const StoreButtonsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  marginTop: theme.spacing(1),
}));

// Rol Seçim Modal Stili
const RoleSelectionModalContainer = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '95%',
  maxWidth: 500,
  padding: 0,
  outline: 'none',
  borderRadius: 16,
  overflow: 'hidden',
}));

const RoleButton = styled(Button)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  padding: theme.spacing(4, 2),
  margin: theme.spacing(2),
  border: `2px solid ${theme.palette.primary.main}`,
  borderRadius: 16,
  backgroundColor: 'transparent',
  color: theme.palette.primary.main,
  transition: 'all 0.3s ease',
  height: '220px',
  width: '100%',
  '&:hover': {
    backgroundColor: 'rgba(13, 71, 161, 0.04)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
    transform: 'translateY(-2px)',
  },
}));

const RoleIcon = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  height: '60px',
  marginBottom: theme.spacing(2),
  color: theme.palette.primary.main,
}));

const RoleTitle = styled(Typography)(({ theme }) => ({
  fontSize: 24,
  fontWeight: 'bold',
  marginBottom: theme.spacing(1),
  color: theme.palette.primary.main,
  height: '36px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const RoleSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 16,
  color: theme.palette.text.secondary,
  textAlign: 'center',
  height: '48px',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: '1.2',
}));

// TypeScript için Google Accounts Client API tanımlaması
interface GoogleAccountsType {
  id: {
    initialize: (options: any) => void;
    prompt: (callback: (notification: PromptMomentNotification) => void) => void;
    cancel: () => void;
  };
}

// Google bildirim tipi
interface PromptMomentNotification {
  isDisplayed: () => boolean;
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

// Google One Tap credential yanıt tipi
interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
  clientId: string;
}

// window objesine Google hesapları özelliği ekle
declare global {
  interface Window {
    google?: {
      accounts: GoogleAccountsType;
    };
  }
}

const Login = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  // Şifre sıfırlama state'leri
  const [resetEmail, setResetEmail] = useState('');
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetSuccess, setResetSuccess] = useState('');
  // CAPTCHA için gerekli state'ler
  const [failedLoginAttempts, setFailedLoginAttempts] = useState(0);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const { openRoleSelection } = usePublicRegistrationFlow();
  const [showGoogleRoleSelectionModal, setShowGoogleRoleSelectionModal] = useState(false); // Google için rol seçim modalı state'i
  const [roleForGoogleSignIn, setRoleForGoogleSignIn] = useState<'admin' | 'employee' | null>(null); // Google için seçilen rol
  const [showCompanyKvkkModal, setShowCompanyKvkkModal] = useState(false); // Google Admin için Şirket Adı & KVKK modalı
  const [googleAdminCompanyName, setGoogleAdminCompanyName] = useState(''); // Google Admin için girilen şirket adı
  const [googleAdminKvkkAccepted, setGoogleAdminKvkkAccepted] = useState(false); // Google Admin için KVKK onayı
  const [isKvkkModalOpen, setIsKvkkModalOpen] = useState(false); // Login içinde de KVKK modalını yönetmek için
  const [showComingSoonMessage, setShowComingSoonMessage] = useState(false); // Yeni state
  const [showAutoLoginNotice, setShowAutoLoginNotice] = useState(false);
  const [pendingAutoLoginCredentials, setPendingAutoLoginCredentials] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [rememberMe, setRememberMe] = useState(true); // Beni Hatırla her zaman aktif (UI'da gizli)
  
  // Google yönlendirme sonuçları için state
  const [googleRedirectProcessing, setGoogleRedirectProcessing] = useState(false);
  const [isGoogleAuthFlow, setIsGoogleAuthFlow] = useState(false);
  
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { login, googleLogin, googleLoginPopup, googleRedirectResult, logout, setGoogleUserData, googleUserData, refreshUserDetails } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const loginFormRef = useRef<HTMLFormElement | null>(null);

  const getPostLoginRedirectPath = (role?: string) => {
    const state = location.state as { from?: { pathname?: string; search?: string; hash?: string } } | undefined;
    const from = state?.from;
    if (!from?.pathname || from.pathname === '/login') return null;

    const normalizedRole = (role || '').toLowerCase();

    // Superadmin/bayi yalnızca kendi paneline (/superadmin...) deep-link edilebilir.
    // Admin rotasına (örn. /e-commerce) yönlendirilirse RoleRoute reddedip aynı
    // state.from ile login'e geri atıyordu → sonsuz döngü, giriş butonu
    // "çalışmıyor" görünüyordu. Rol varsayılanına (dashboard / şirketler) düşür.
    if (
      (normalizedRole === 'superadmin' || normalizedRole === 'dealer') &&
      !/^\/superadmin(\/|$)/i.test(from.pathname)
    ) {
      return null;
    }

    const isManagementRole =
      normalizedRole === 'admin' || normalizedRole === 'subadmin' ||
      normalizedRole === 'superadmin' || normalizedRole === 'manager';
    const isWorkerRole = normalizedRole === 'employee' || normalizedRole === 'courier';
    const isWmsWorkerRoute = /^\/wms\/(toplama|paketleme|malkabul|stoksayim|secim)(\/|$)/i.test(from.pathname);

    // Admin/manager kullanicilari login'de isci WMS ekranlarina deep-link ile dusurme.
    if (isManagementRole && isWmsWorkerRoute) return null;
    // Employee/courier icin de WMS deep-link'i login asamasinda atla.
    // Boylece coklu yetkide her login'de secim ekrani kurali uygulanir.
    if (isWorkerRole && isWmsWorkerRoute) return null;

    const search = from.search || '';
    const hash = from.hash || '';
    return `${from.pathname}${search}${hash}`;
  };

  const tryNavigateToWmsScreens = async (_companyId: string, userId: string): Promise<boolean> => {
    try {
      const perms = await getScreenPermissionsByType(userId, 'wmsScreens');
      const activeWmsScreenIds = perms.filter(r => r.isEnabled && r.screenId).map(r => String(r.screenId));

      if (activeWmsScreenIds.length === 0) return false;

      if (activeWmsScreenIds.length > 0) {
        navigate('/wms/secim');
        return true;
      }
    } catch (err) {
      console.error('[Login.tsx] WMS yonlendirme kontrolunde hata:', err);
    }

    return false;
  };

  // PYK ekranı personel ataması artık SQL'de (PykScreen.SettingsJson.assignedPersonnel) tutuluyor;
  // RTDB'deki permissions/pykScreens kullanılmıyor. Bu yüzden SQL'den kontrol edip yönlendiriyoruz.
  const tryNavigateToSqlPykScreen = async (userId: string): Promise<boolean> => {
    try {
      const pykScreens = await salesService.getPykScreens();
      for (const screen of pykScreens || []) {
        if (!screen?.SettingsJson) continue;
        let settings: any;
        try {
          settings = JSON.parse(screen.SettingsJson);
        } catch {
          continue;
        }
        const assigned: any[] = settings?.assignedPersonnel || [];
        if (Array.isArray(assigned) && assigned.map(String).includes(String(userId))) {
          console.log(`[Login.tsx] SQL PYK ekranına yönlendiriliyor: ${screen.Id}`);
          navigate(`/pyk-ekrani/${screen.Id}`);
          return true;
        }
      }
    } catch (err) {
      console.error('[Login.tsx] SQL PYK ekran kontrolünde hata:', err);
    }
    return false;
  };

  // quickSaleScreens yetkilerini SQL'den (SystemUserScreenPermissions) okur.
  // Aktif (isEnabled=true) screenId listesini doner.
  const getActiveQuickSaleScreenIds = async (_companyId: string, userId: string): Promise<string[]> => {
    let activeScreenIds: string[] = [];
    try {
      const perms = await getScreenPermissionsByType(userId, 'quickSaleScreens');
      activeScreenIds = perms.filter(r => r.isEnabled && r.screenId).map(r => String(r.screenId));
    } catch (e) {
      console.warn('[Login.tsx] SQL quickSaleScreens yetki okuma hatasi:', e);
    }
    return activeScreenIds;
  };

  // Bir screenType (kitchenScreens/poleDisplayScreens/callCenterScreens) icin
  // screenId->settings haritasini SQL'den (SystemUserScreenPermissions) okur.
  // Login yonlendirmeleri bunu kullanir.
  const getRedirectPermMap = async (
    _companyId: string,
    userId: string,
    screenType: string
  ): Promise<Record<string, any>> => {
    const map: Record<string, any> = {};
    try {
      const rows = await getScreenPermissionsByType(userId, screenType);
      rows.forEach(r => {
        if (r.isEnabled && r.screenId != null) map[String(r.screenId)] = r.settings ?? true;
      });
    } catch (e) {
      console.warn(`[Login.tsx] SQL ${screenType} yetki okuma hatasi:`, e);
    }
    return map;
  };

  // Resim tıklama mesajları için state
  const [imageMessage, setImageMessage] = useState('');
  const [showImageMessage, setShowImageMessage] = useState(false);
  
  // Google ile giriş için state'ler
  const [googleLoading, setGoogleLoading] = useState(false);
  
  // Google One Tap için DOM elemanı referansı
  const googleButtonRef = useRef(null);
  const [googleInitialized, setGoogleInitialized] = useState(false);

  // ENSTRUMANTASYON: Login component lifecycle + sayfa yenileme/kapanma takibi
  // Bu, "göz kırpıp olduğun yerde kalma" bug'inin kanitini toplamak icin eklendi.
  // Eger sayfa beklenmedik sekilde yenileniyorsa beforeunload tetiklenir; SPA navigate ise tetiklenmez.
  useEffect(() => {
    console.log('🟢 [Login] MOUNT', {
      url: window.location.href,
      isInIframe: window.parent !== window,
      pathname: window.location.pathname,
      hash: window.location.hash,
      timestamp: new Date().toISOString()
    });

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      // Yalnizca bilgi amacli; "returnValue" set etmiyoruz, kullaniciyi engellemiyoruz.
      console.warn('🟠 [Login] beforeunload — sayfa yenileniyor/kapaniyor', {
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    };
    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      console.log('🔴 [Login] UNMOUNT', {
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Google OAuth script yükle
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  // Google One Tap'i başlat - DEVRE DIŞI BIRAKILDI
  // useEffect(() => {
  //   // Sayfa yüklendiğinde sadece bir kez initialize edilmeli
  //   if (!googleInitialized) {
  //     const initializeGoogleOneTap = () => {
  //       if (window.google && window.google.accounts) {
  //         try {
  //           window.google.accounts.id.initialize({
  //             client_id: "338071169045-4kj277asrv67kp1u8mtn84p4gvo3pubh.apps.googleusercontent.com",
  //             callback: handleGoogleOneTapResponse,
  //             context: "signin",
  //             use_fedcm_for_prompt: true, // FedCM API'sini kullan
  //             auto_select: false, // Otomatik seçimi devre dışı bırak, kullanıcı seçmeli
  //             cancel_on_tap_outside: true // Dışarı tıklandığında iptal et
  //           });
  //           setGoogleInitialized(true);
  //           
  //           // One Tap prompt'u otomatik gösterme - kullanıcı butona tıklayacak
  //           // window.google.accounts.id.prompt((notification) => {
  //           //   if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
  //           //     console.log('Google One Tap gösterilemiyor, sebep:', 
  //           //       notification.getNotDisplayedReason() || notification.getSkippedReason());
  //           //   }
  //           // });
  //         } catch (error) {
  //           console.error('Google One Tap başlatma hatası:', error);
  //         }
  //       }
  //     };

  //     // Google One Tap skriptini yükle
  //     if (!document.querySelector('script[src="https://accounts.google.com/gsi/client"]')) {
  //       const script = document.createElement('script');
  //       script.src = 'https://accounts.google.com/gsi/client';
  //       script.onload = initializeGoogleOneTap;
  //       script.async = true;
  //       script.defer = true;
  //       document.body.appendChild(script);
  //     } else {
  //       // Script zaten yüklenmişse doğrudan başlat
  //       initializeGoogleOneTap();
  //     }
  //   }

  //   return () => {
  //     // Component çıkarken Google One Tap'i iptal et
  //     if (window.google && window.google.accounts) {
  //       window.google.accounts.id.cancel();
  //     }
  //   };
  // }, [googleInitialized]); // googleInitialized değiştiğinde tekrar çalışsın

  // Google One Tap'ten dönen yanıtı işle
  const handleGoogleOneTapResponse = async (response: GoogleCredentialResponse) => {
    try {
      console.log('Google One Tap cevap:', response);
      setError('');

      const idToken = response.credential;
      const credential = GoogleAuthProvider.credential(idToken);
      const result = await signInWithCredential(auth, credential);
      const firebaseUser = result.user;
      
      if (!firebaseUser) {
        throw new Error('Giriş başarılı oldu ancak Firebase kullanıcı bilgileri alınamadı.');
      }
      
      const existingUserData = await firebaseDataService.getUserData(firebaseUser.uid);
      
      if (!existingUserData) {
        console.log("[handleGoogleOneTapResponse] Kullanıcı DB'de yok. Google için seçilen rol ile yeni kayıt yapılacak. Seçilen rol:", roleForGoogleSignIn);

        if (!roleForGoogleSignIn) {
          console.error("[handleGoogleOneTapResponse] Google için rol seçilmemiş! Bu bir hata durumudur.");
          setError(t('loginPage.internalErrors.googleRoleNotSelectedError'));
          setIsGoogleAuthFlow(false); 
          setGoogleLoading(false);
          return;
        }

        let firstName = '', lastName = '';
        if (firebaseUser.displayName) {
          const nameParts = firebaseUser.displayName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }

        const newUserDetails: UserDetails = {
          id: firebaseUser.uid,
          email: firebaseUser.email!,
          firstName: firstName,
          lastName: lastName,
          role: roleForGoogleSignIn,
          createdAt: Date.now(),
          photoURL: firebaseUser.photoURL,
          // companyId ve companyName rol admin ise aşağıda eklenecek
        };

        if (roleForGoogleSignIn === 'admin') {
          const companyName = googleAdminCompanyName; // YENİ: Kullanıcının girdiği şirket adı

          if (!companyName || companyName.trim() === '') {
            console.error("[handleGoogleOneTapResponse] Admin kaydı için şirket adı girilmemiş! Bu bir hata durumudur.");
            setError(t('loginPage.internalErrors.companyNameMissingError'));
            setIsGoogleAuthFlow(false); 
            setGoogleLoading(false);
            return;
          }

          // Faz 1: Yeni sirket SQL'de SqlOnly olarak olusturulur (Firebase'e companies/{id}/info YAZILMAZ).
          // Admin kullanici yazimi (users/{uid}) mevcut haliyle KALIR (Faz 3'te SQL'e tasinacak).
          const companyId = await createCompany({
            name: companyName,
            adminFirebaseUid: firebaseUser.uid,
            maxPersonnel: 5,
          });

          if (!companyId) {
            throw new Error('Şirket ID oluşturulamadı');
          }

          newUserDetails.companyId = companyId;
          newUserDetails.companyName = companyName;
          newUserDetails.kvkkAccepted = googleAdminKvkkAccepted; // KVKK onay durumu kullanıcıya da eklendi (isteğe bağlı)
          
          await authApi.post('/api/auth/sync-user', {
            FirebaseUid: firebaseUser.uid,
            Email: newUserDetails.email,
            Password: 'GOOGLE_AUTH',
            FirstName: newUserDetails.firstName || '',
            LastName: newUserDetails.lastName || '',
            Role: newUserDetails.role,
            CompanyId: newUserDetails.companyId || null,
            CompanyName: newUserDetails.companyName || null,
            DeviceType: 'web',
            ProfileImageUrl: newUserDetails.photoURL || null,
            KvkkAccepted: newUserDetails.kvkkAccepted || false
          });
          console.log(`Google ile yeni YÖNETİCİ hesabı (${companyName}) oluşturuldu, muhasebe dashboard'a yönlendiriliyor.`);
          navigate('/accounting/dashboard');

        } else if (roleForGoogleSignIn === 'employee') {
          // Personel kaydında KVKK onayı gerekirse burada da eklenebilir.
          // Şimdilik personel için KVKK modalı akışta yok.
          await authApi.post('/api/auth/sync-user', {
            FirebaseUid: firebaseUser.uid,
            Email: newUserDetails.email,
            Password: 'GOOGLE_AUTH',
            FirstName: newUserDetails.firstName || '',
            LastName: newUserDetails.lastName || '',
            Role: newUserDetails.role,
            CompanyId: newUserDetails.companyId || null,
            CompanyName: newUserDetails.companyName || null,
            DeviceType: 'web',
            ProfileImageUrl: newUserDetails.photoURL || null,
            KvkkAccepted: newUserDetails.kvkkAccepted || false
          });
          console.log("Google ile yeni PERSONEL hesabı oluşturuldu, EmployeeModal gösterilecek.");
          setShowEmployeeModal(true); 
        }

      } else {
        // Kullanıcı veritabanında VAR - Normal giriş ve yönlendirme
        console.log("[handleGoogleOneTapResponse] Kullanıcı DB'de var. Rolüne göre yönlendiriliyor.");
        const role = existingUserData.role || 'employee';
        const companyId = existingUserData.companyId;
        
        if (role === 'superadmin' || role === 'dealer') {
          navigate(getPanelHomePath(role));
        } else if (role === 'admin') {
          navigate('/accounting/dashboard');
        } else if (role === 'subadmin') {
          // Alt admin: SubAdminModuleGuard izinli ilk modüle yönlendirir.
          navigate('/dashboard');
        } else if (role === 'manager') {
          navigate('/manager');
        } else if (role === 'employee') {
          if (!companyId) {
            await auth.signOut(); 
            setShowEmployeeModal(true);
          } else {
            // Faz 3: quickSaleScreens yetkisi SQL'den (HybridFallback RTDB).
            const activeScreenIds = await getActiveQuickSaleScreenIds(companyId, firebaseUser.uid);
            if (activeScreenIds.length > 0) {
                const firstScreenId = activeScreenIds[0];
                // ✅ YENİ: ScreenType kontrolü yap
                try {
                  const screenResponse = await salesApi.get(`/api/QuickSaleScreen/${firstScreenId}`);
                  const screenData = screenResponse.data;
                  const screenType = screenData.ScreenType || screenData.screenType || 'Standalone';
                  
                  if (screenType.toLowerCase() === 'restaurant') {
                    const restaurantScreenId = screenData.RestaurantScreenId || screenData.restaurantScreenId;
                    if (restaurantScreenId) {
                      navigate(`/restaurant-screen/${restaurantScreenId}`);
                    } else {
                      navigate(`/hizli-satis/ekran/${firstScreenId}`);
                    }
                  } else if (screenType.toLowerCase() === 'package') {
                    navigate(`/paket-ekrani/${firstScreenId}`);
                  } else {
                    navigate(`/hizli-satis/ekran/${firstScreenId}`);
                  }
                } catch (error) {
                  console.error('[Login.tsx] Google One Tap - ScreenType kontrolü hatası:', error);
                  navigate(`/hizli-satis/ekran/${firstScreenId}`);
                }
            } else {
              await checkPykPermissions(firebaseUser.uid, companyId);
            }
          }
        } else {
          await auth.signOut();
          setError(t('loginPage.internalErrors.unexpectedRoleError'));
        }
      }
    } catch (error) {
      console.error('Google One Tap giriş veya sonrası işleme hatası:', error);
      setError(t('loginPage.genericError'));
    } finally {
      setGoogleLoading(false); // Yükleniyor durumunu bitir
      setIsGoogleAuthFlow(false); // Google akışını sonlandır
      setRoleForGoogleSignIn(null); // Seçilen rolü sıfırla
    }
  };

  // Kaydedilmiş hatalı giriş sayısını kontrol et
  useEffect(() => {
    const storedAttempts = localStorage.getItem('failedLoginAttempts');
    const lastFailedTimestamp = localStorage.getItem('lastFailedLoginTimestamp');
    
    if (storedAttempts && lastFailedTimestamp) {
      const lastAttemptTime = parseInt(lastFailedTimestamp);
      const currentTime = new Date().getTime();
      
      // Son hatanın üzerinden 30 dakika geçtiyse sıfırla
      if (currentTime - lastAttemptTime > 30 * 60 * 1000) {
        localStorage.removeItem('failedLoginAttempts');
        localStorage.removeItem('lastFailedLoginTimestamp');
        setFailedLoginAttempts(0);
        setShowCaptcha(false);
      } else {
        const attempts = parseInt(storedAttempts);
        setFailedLoginAttempts(attempts);
        setShowCaptcha(attempts >= 3);
      }
    }
  }, []);

  // Kaydedilmiş kullanıcı bilgilerini yükle (Beni Hatırla özelliği)
  useEffect(() => {
    try {
      const rememberedCredentials = localStorage.getItem('rememberedCredentials');
      if (rememberedCredentials) {
        const credentials = JSON.parse(rememberedCredentials);
        if (credentials.email && credentials.password && credentials.rememberMe) {
          setEmail(credentials.email);
          setPassword(credentials.password);
          setRememberMe(true);
          console.log('Kaydedilmiş kullanıcı bilgileri yüklendi:', credentials.email);
        }
      }
    } catch (error) {
      console.error('Kaydedilmiş kullanıcı bilgileri yüklenirken hata:', error);
      // Hatalı veri varsa temizle
      localStorage.removeItem('rememberedCredentials');
    }
  }, []);

  // Register -> Login otomatik doldur + otomatik giriş akışı
  useEffect(() => {
    const state = location.state as
      | {
          autoLogin?: boolean;
          credentials?: { email?: string; password?: string };
        }
      | undefined;

    if (!state?.autoLogin || !state.credentials?.email || !state.credentials?.password) {
      return;
    }

    setEmail(state.credentials.email);
    setPassword(state.credentials.password);
    setRememberMe(true);
    setShowAutoLoginNotice(true);
    setPendingAutoLoginCredentials({
      email: state.credentials.email,
      password: state.credentials.password,
    });

    // State'i tek seferlik temizle (geri/yenilemede tekrar denemesin)
    navigate('/login', { replace: true, state: null });

    const noticeTimer = window.setTimeout(() => {
      setShowAutoLoginNotice(false);
    }, 2800);

    return () => {
      window.clearTimeout(noticeTimer);
    };
  }, [location.state, navigate]);

  // Değerler state'e gerçekten oturduğunda otomatik giriş yap
  useEffect(() => {
    if (!pendingAutoLoginCredentials) return;
    if (
      email !== pendingAutoLoginCredentials.email ||
      password !== pendingAutoLoginCredentials.password
    ) {
      return;
    }

    console.log('🟣 [Login] auto-login tetikleniyor (150ms timer)', {
      email,
      timestamp: new Date().toISOString()
    });

    // null'a çekmeyi timer içinde yap — dışarıda yapmak cleanup'ı tetikleyip timer'ı iptal ediyor
    const timer = window.setTimeout(() => {
      setPendingAutoLoginCredentials(null);
      console.log('🟣 [Login] auto-login → handleSubmit çağrılıyor');
      handleSubmit({ preventDefault: () => {} } as FormEvent);
    }, 150);

    return () => window.clearTimeout(timer);
  }, [pendingAutoLoginCredentials, email, password]);

  // Yerel servis iframe iletişimi için mesaj dinleyicisi
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Güvenlik kontrolü - sadece yerel servis domain'lerinden gelen mesajları kabul et
      const allowedOrigins = [
        'http://localhost:3003',
        'http://127.0.0.1:3003',
        'file://', // Electron uygulaması için
      ];
      
      const isAllowedOrigin = allowedOrigins.some(origin => 
        event.origin === origin || event.origin.startsWith('file://')
      );
      
      if (!isAllowedOrigin) {
        return;
      }
      
      console.log('📨 [Login.tsx] Parent window\'dan mesaj alındı:', {
        type: event.data?.type,
        origin: event.origin,
        hasCredentials: !!event.data?.credentials,
        timestamp: new Date().toISOString()
      });
      
      if (event.data.type === 'LOAD_REMEMBERED_CREDENTIALS' && event.data.credentials) {
        const credentials = event.data.credentials;
        if (credentials.email && credentials.password && credentials.rememberMe) {
          setEmail(credentials.email);
          setPassword(credentials.password);
          setRememberMe(true);
          console.log('[Login.tsx] Yerel servisten kullanıcı bilgileri yüklendi:', credentials.email);
        }
      } else if (event.data.type === 'CLEAR_ALL_STORAGE') {
        console.warn('🟠 [Login.tsx] CLEAR_ALL_STORAGE alındı — sayfa 2sn sonra yenilenecek!', {
          origin: event.origin,
          stack: new Error('CLEAR_ALL_STORAGE trigger').stack
        });
        // Tüm localStorage ve sessionStorage'ı temizle
        try {
          localStorage.clear();
          sessionStorage.clear();
          
          // Form alanlarını da temizle
          setEmail('');
          setPassword('');
          setRememberMe(false);
          setError('');
          
          console.log('[Login.tsx] Tüm storage temizlendi ve form sıfırlandı');
          
          // Kullanıcıya bilgi ver
          setError('Tüm yerel veriler temizlendi. Sayfa yeniden yüklenecek.');
          
          // 2 saniye sonra sayfayı yenile
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } catch (error) {
          console.error('[Login.tsx] Storage temizleme hatası:', error);
        }
      } else {
        console.log('ℹ️ [Login.tsx] Bilinmeyen mesaj tipi (yok sayıldı):', event.data?.type);
      }
    };

    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);
  
  // CAPTCHA doğrulama işleyicisi
  const handleCaptchaVerify = (token: string | null) => {
    if (token) {
      setCaptchaVerified(true);
    } else {
      setCaptchaVerified(false);
    }
  };
  
  // CAPTCHA süresi dolduğunda
  const handleCaptchaExpired = () => {
    setCaptchaVerified(false);
  };

  // Geliştirme ortamında CAPTCHA kontrolü
  const isDevelopment = window.location.hostname === 'localhost' || 
                       window.location.hostname === '127.0.0.1';

  // Geliştirme ortamında CAPTCHA'yı kontrol et ve otomatik doğrula
  useEffect(() => {
    if (isDevelopment && showCaptcha) {
      console.log('Geliştirme ortamında CAPTCHA otomatik olarak doğrulandı');
      setCaptchaVerified(true);
    }
  }, [showCaptcha, isDevelopment]);

  const handleGooglePlayClick = () => {
    window.open('https://play.google.com/store/apps/details?id=com.mt_teknoloji.gts', '_blank');
  };

  const handleAppStoreClick = () => {
    window.open('https://apps.apple.com/tr/app/gts-g%C3%B6rev-takip-sistemi/id6743622025?l=tr&platform=iphone', '_blank');
  };

  const handleCloseEmployeeModal = () => {
    setShowEmployeeModal(false);
  };

  const handleForgotPasswordClick = () => {
    setResetEmail('');
    setResetError('');
    setResetSuccess('');
    setShowResetModal(true);
  };

  const handleCloseResetModal = () => {
    setShowResetModal(false);
  };

  const handlePasswordReset = async () => {
    // Form doğrulama
    if (!resetEmail) {
      setResetError(t('loginPage.resetPassword.emailRequiredError'));
      return;
    }
    
    if (!resetEmail.includes('@')) {
      setResetError(t('loginPage.resetPassword.invalidEmailError'));
      return;
    }
    
    setResetLoading(true);
    setResetError('');
    setResetSuccess('');
    
    try {
      // Own-auth: şifre sıfırlama talebi. Backend GÜVENLİK gereği email var/yok
      // bilgisini sızdırmaz; her durumda generic başarı döner. Auth header gerekmez.
      await authApi.post('/api/auth/forgot-password', { email: resetEmail });
      setResetSuccess(t('loginPage.resetPassword.successMessage'));
      
      // 3 saniye sonra modalı kapat
      setTimeout(() => {
        setShowResetModal(false);
      }, 3000);
    } catch (err: any) {
      // Generic başarı backend tarafında garanti; ağ/sunucu hatası olursa kullanıcıyı bilgilendir.
      console.error('Şifre sıfırlama hatası:', err);
      setResetError(t('loginPage.resetPassword.sendError'));
    } finally {
      setResetLoading(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    // ENSTRÜMANTASYON: handleSubmit cagrildi
    const submitId = Math.random().toString(36).slice(2, 8);
    console.log(`🔵 [Login][${submitId}] step:0 handleSubmit BAŞLADI`, {
      loading,
      hasEmail: !!email,
      hasPassword: !!password,
      showCaptcha,
      captchaVerified,
      isInIframe: window.parent !== window,
      timestamp: new Date().toISOString()
    });

    // ÇİFT-SUBMIT KORUMASI: loading=true iken yeni submit'i red et
    // (Enter tuşu, hızlı çift tıklama, autoLogin useEffect ile manuel tıklama çakışması vb.)
    if (loading) {
      console.warn(`⚠️ [Login][${submitId}] step:0a ÇİFT SUBMIT engellendi (loading=true)`);
      return;
    }

    // Form doğrulama
    if (!email) {
      console.warn(`⚠️ [Login][${submitId}] step:0b email boş, erken return`);
      setError(t('loginPage.resetPassword.emailRequiredError'));
      return;
    }
    
    if (!email.includes('@')) {
      console.warn(`⚠️ [Login][${submitId}] step:0c email geçersiz, erken return`);
      setError(t('loginPage.resetPassword.invalidEmailError'));
      return;
    }
    
    if (!password) {
      console.warn(`⚠️ [Login][${submitId}] step:0d şifre boş, erken return`);
      setError(t('loginPage.passwordLabel'));
      return;
    }
    
    // CAPTCHA kontrol et
    if (showCaptcha && !captchaVerified) {
      console.warn(`⚠️ [Login][${submitId}] step:0e CAPTCHA gerekli ama doğrulanmamış, erken return`);
      setError(t('loginPage.captchaError'));
      return;
    }
    
    setLoading(true);
    setError('');
    
    try {
      console.log(`🔵 [Login][${submitId}] step:1 own-auth login çağrılıyor...`);
      // own-auth: context.login oturum kurar, currentUser shim'i set eder ve
      // userDetails'i SQL'den yükler. Dönen AuthUser shim'i uid/email sağlar.
      const user = await login(email, password);
      console.log(`✅ [Login][${submitId}] step:2 own-auth login OK`, {
        currentUserUid: user?.uid,
        email: user?.email,
      });

      if (!user) {
        console.error(`❌ [Login][${submitId}] step:2a own-auth login user NULL`);
        throw new Error('Giriş başarılı oldu ancak kullanıcı bilgileri alınamadı.');
      }
      
      // Kullanıcı verilerini veritabanından al
      // 🚀 ÖNCE: Cloud Functions üzerinden çek
      // 🔄 1. FALLBACK: Cloud Functions cache'siz tekrar dene (orijinal TR davranisi)
      // 🔄 2. FALLBACK: Direkt Firebase Realtime DB (Cloud Functions deploy edilmemisse — orn. USA modu)
      let userData: any = null;
      try {
        console.log(`📡 [Login][${submitId}] step:3 Cloud Functions getUserData (cache) çağrılıyor...`);
        userData = await firebaseDataService.getUserData(user.uid);
        if (userData) {
          console.log(`✅ [Login][${submitId}] step:3a userData alındı:`, userData);
        }
      } catch (cloudError) {
        console.warn(`⚠️ [Login][${submitId}] step:3b Cloud Functions hatası, fallback kullanılıyor:`, cloudError);
      }

      // Fallback 1: Cloud Functions cache'siz tekrar dene (orijinal davranis — TR icin korundu)
      if (!userData) {
        console.log(`🔄 [Login][${submitId}] step:3c Fallback: cache'siz tekrar deneniyor...`);
        try {
          userData = await firebaseDataService.getUserData(user.uid, false);
          if (userData) {
            console.log(`✅ [Login][${submitId}] step:3d Cache'siz Cloud Functions ile userData alındı`);
          }
        } catch (cloudRetryError) {
          console.warn(`⚠️ [Login][${submitId}] step:3d-err Cache'siz Cloud Functions de hata verdi, direkt DB fallback'e gidiliyor:`, cloudRetryError);
        }
      }

      // NOT: Eski "direkt RTDB users/{uid}" fallback'i Faz A'da kaldirildi
      // (getUserData artik SQL/gts_auth_api uzerinden okur).

      if (!userData) {
        // Kullanıcı veritabanında yoksa çıkış yap
        console.error(`❌ [Login][${submitId}] step:3g userData hâlâ null — logout + throw`);
        await logout();
        throw new Error('Kullanıcı profili bulunamadı. Lütfen yönetici ile iletişime geçin.');
      }
      const role = userData.role || 'employee';
      const companyId = userData.companyId; // Şirket ID'sini al

      // NOT: Login activity kaydı artık .NET auth API'sinde (sync-user) sunucu tarafında yapılıyor
      // (CompanyLoginActivities / PersonnelLoginActivities). Eski Node /api/login/track çağrısı kaldırıldı.

      // ✅ Firebase → SQL Kullanıcı Senkronizasyonu (fire-and-forget, login'i bloke etmez)
      authApi.post('/api/auth/sync-user', {
        FirebaseUid: user.uid,
        Email: email,
        Password: password,
        FirstName: userData.firstName || userData.name?.split(' ')[0] || '',
        LastName: userData.lastName || userData.name?.split(' ').slice(1).join(' ') || '',
        Phone: userData.phone || userData.phoneNumber || null,
        Role: userData.role || 'employee',
        CompanyId: userData.companyId || null,
        CompanyName: userData.companyName || null,
        BranchesId: userData.branchesId || userData.branchId || null,
        EmployeeId: userData.employeeId || null,
        FcmToken: userData.fcmToken || null,
        DeviceType: 'web',
        ProfileImageUrl: userData.profileImageUrl || userData.photoURL || null,
        HasDeletionRequest: userData.deletionRequest?.hasDeletionRequest || false,
        DeletionRequestDate: userData.deletionRequest?.requestDate || null,
        DeletionRequestId: userData.deletionRequest?.requestId || null,
        KvkkAccepted: userData.kvkkAccepted || false
      })
        .then(res => console.log('✅ [SQL Sync] Senkronize edildi:', res.data))
        .catch(err => console.warn('⚠️ [SQL Sync] Network hatası (kritik değil):', err));
      
      // Hatalı giriş sayacını sıfırla
      localStorage.removeItem('failedLoginAttempts');
      localStorage.removeItem('lastFailedLoginTimestamp');
      setFailedLoginAttempts(0);
      setShowCaptcha(false);

      // Beni Hatırla seçiliyse kullanıcı bilgilerini kaydet
      if (rememberMe) {
        const credentialsToSave = {
          email: email,
          password: password,
          rememberMe: true,
          savedAt: new Date().toISOString()
        };
        localStorage.setItem('rememberedCredentials', JSON.stringify(credentialsToSave));
        console.log('Kullanıcı bilgileri kaydedildi (Beni Hatırla)');
        
        // Yerel servise de bilgileri kaydetmesi için mesaj gönder
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'SAVE_CREDENTIALS',
            credentials: credentialsToSave
          }, '*');
          console.log('[Login.tsx] Yerel servise kullanıcı bilgileri gönderildi');
        }
      } else {
        // Beni Hatırla seçili değilse kaydedilmiş bilgileri temizle
        localStorage.removeItem('rememberedCredentials');
        console.log('Kaydedilmiş kullanıcı bilgileri temizlendi');
        
        // Yerel servise de bilgileri temizlemesi için mesaj gönder
        if (window.parent && window.parent !== window) {
          window.parent.postMessage({
            type: 'REMOVE_CREDENTIALS'
          }, '*');
          console.log('[Login.tsx] Yerel servise kullanıcı bilgileri temizleme mesajı gönderildi');
        }
      }
      
      // Kullanıcı rolü kontrolü ve yönlendirme
      const redirectPath = getPostLoginRedirectPath(role);
      console.log(`🔵 [Login][${submitId}] step:4 yönlendirme kararı`, {
        role,
        companyId,
        redirectPath,
        locationStateFrom: (location.state as any)?.from?.pathname
      });
      if (redirectPath) {
        console.log(`✅ [Login][${submitId}] step:4a deep-link redirect → ${redirectPath}`);
        setLoading(false);
        navigate(redirectPath, { replace: true });
        return;
      }

      if (role === 'superadmin' || role === 'dealer') {
        const homePath = getPanelHomePath(role);
        console.log(`✅ [Login][${submitId}] step:4b ${role} → ${homePath}`);
        setLoading(false);
        navigate(homePath);
        return;
      } else if (role === 'admin') {
        console.log(`✅ [Login][${submitId}] step:4c admin → /accounting/dashboard`);
        setLoading(false);
        navigate('/accounting/dashboard');
        return;
      } else if (role === 'subadmin') {
        // Alt admin: admin bloğuna gönder — SubAdminModuleGuard (App.tsx) izin haritasını
        // yükleyip izinli ilk modüle yönlendirir; hiç izin yoksa bilgi ekranı gösterir.
        console.log(`✅ [Login][${submitId}] step:4c2 subadmin → /dashboard (modül guard yönlendirir)`);
        setLoading(false);
        navigate('/dashboard');
        return;
      } else if (role === 'manager') {
        console.log(`✅ [Login][${submitId}] step:4d manager → /manager`);
        setLoading(false);
        navigate('/manager');
        return;
      } else if (role === 'courier') {
        // Kurye rolü için personel ile aynı yetki kontrollerini yap
        if (!companyId) {
            // Şirkete dahil olmayan kuryeleri direkt personel görev panosuna yönlendir
            navigate('/personnel/tasks');
            setLoading(false);
            return;
        }

        try {
            console.log(`[Login.tsx] Kurye ${user.email} (UID: ${user.uid}) yetki kontrolü başlatılıyor`);
            console.log(`[Login.tsx] CompanyId: ${companyId}, User UID: ${user.uid}`);
            
            // Faz 3: quickSaleScreens yetkisi SQL'den (HybridFallback RTDB).
            const activeScreenIds = await getActiveQuickSaleScreenIds(companyId, user.uid);
            console.log('[Login.tsx] Aktif ekran ID\'leri:', activeScreenIds);
            if (activeScreenIds.length > 0) {
                    const firstScreenId = activeScreenIds[0];
                    console.log(`[Login.tsx] İlk ekran ID: ${firstScreenId}`);
                    
                    // ✅ YENİ: Ekranın ScreenType'ını kontrol et (Restaurant mi, Standalone mi?)
                    try {
                        const screenResponse = await salesApi.get(`/api/QuickSaleScreen/${firstScreenId}`);
                        const screenData = screenResponse.data;
                        console.log('[Login.tsx] 📱 KURYE - API\'den gelen ekran verisi:', JSON.stringify(screenData, null, 2));
                        
                        const screenType = screenData.ScreenType || screenData.screenType || screenData.screentype || 'Standalone';
                        console.log(`[Login.tsx] 📱 KURYE - Tespit edilen ScreenType: ${screenType}`);
                        
                        if (screenType.toLowerCase() === 'restaurant') {
                            const restaurantScreenId = screenData.RestaurantScreenId || 
                                                       screenData.restaurantScreenId || 
                                                       screenData.RestaurantScreenID || 
                                                       screenData.restaurantscreenid;
                            
                            console.log(`[Login.tsx] 📱 KURYE - RestaurantScreenId (tüm varyantlar kontrol edildi):`, {
                                RestaurantScreenId: screenData.RestaurantScreenId,
                                restaurantScreenId: screenData.restaurantScreenId,
                                RestaurantScreenID: screenData.RestaurantScreenID,
                                restaurantscreenid: screenData.restaurantscreenid,
                                finalValue: restaurantScreenId
                            });
                            
                            if (restaurantScreenId) {
                                console.log(`[Login.tsx] 📱 ✅ KURYE restoran ekranına yönlendiriliyor: /restaurant-screen/${restaurantScreenId}`);
                                navigate(`/restaurant-screen/${restaurantScreenId}`);
                            } else {
                                console.error('[Login.tsx] 📱 ❌ RestaurantScreenId bulunamadı! API yanıtı:', screenData);
                                console.warn('[Login.tsx] 📱 ⚠️ Fallback: hızlı satış ekranına yönlendiriliyor');
                                navigate(`/hizli-satis/ekran/${firstScreenId}`);
                            }
                        } else if (screenType.toLowerCase() === 'package') {
                            console.log(`[Login.tsx] 📦 ✅ KURYE paket sipariş ekranına yönlendiriliyor: /paket-ekrani/${firstScreenId}`);
                            navigate(`/paket-ekrani/${firstScreenId}`);
                        } else {
                            console.log(`[Login.tsx] 📱 ✅ KURYE hızlı satış ekranına yönlendiriliyor: /hizli-satis/ekran/${firstScreenId}`);
                            navigate(`/hizli-satis/ekran/${firstScreenId}`);
                        }
                    } catch (apiError) {
                        console.error('[Login.tsx] ❌ API çağrısı hatası:', apiError);
                        // Hata durumunda varsayılan olarak hızlı satışa yönlendir
                        console.log(`[Login.tsx] Hata nedeniyle varsayılan hızlı satışa yönlendiriliyor: /hizli-satis/ekran/${firstScreenId}`);
                        navigate(`/hizli-satis/ekran/${firstScreenId}`);
                    }
                    
                    setLoading(false);
                    return;
                }
            
            // Faz 4: mutfak yetkisi SQL'den (HybridFallback RTDB).
            {
                const permittedKitchenScreens = await getRedirectPermMap(companyId, user.uid, 'kitchenScreens');
                for (const menuId of Object.keys(permittedKitchenScreens)) {
                    const permissions = permittedKitchenScreens[menuId];
                    if (permissions?.accessKitchenScreen === true) {
                        console.log(`[Login.tsx] Kurye mutfak ekranına yönlendiriliyor: ${menuId}`);
                        navigate(`/mutfak-ekrani/${menuId}`, { state: { fromLogin: true } });
                        setLoading(false);
                        return;
                    }
                }
            }
            
            // Faz 4: musteri ekrani (Pole Display) yetkisi SQL'den (HybridFallback RTDB).
            {
                const permittedPoleDisplayScreens = await getRedirectPermMap(companyId, user.uid, 'poleDisplayScreens');
                for (const menuId of Object.keys(permittedPoleDisplayScreens)) {
                    const permissions = permittedPoleDisplayScreens[menuId];
                    if (permissions?.accessPoleDisplay === true && permissions?.autoRedirect === true) {
                        console.log(`[Login.tsx] Kurye müşteri ekranına yönlendiriliyor: ${menuId}`);
                        navigate(`/musteri-ekrani/${menuId}`, { state: { fromLogin: true } });
                        setLoading(false);
                        return;
                    }
                }
            }
            
            // SQL'deki PYK ekran ataması kontrolü (yeni sistem — atama SQL'de tutuluyor)
            if (await tryNavigateToSqlPykScreen(user.uid)) {
                return;
            }

            // Hiçbir özel yetki yoksa görev panosuna yönlendir
            console.log(`[Login.tsx] Kurye özel yetkisi yok, görev panosuna yönlendiriliyor`);
            navigate('/personnel/tasks');
            setLoading(false);
            
        } catch (permissionError) {
            console.error("Kurye yetkileri kontrol edilirken hata:", permissionError);
            await logout();
            setError("Yetki kontrolü sırasında bir hata oluştu. Tekrar deneyin.");
            setLoading(false);
            return;
        }

      } else if (role === 'employee') {
        // Personel rolü için Hızlı Satış yetkilerini kontrol et
        if (!companyId) {
            // Personel görev panosuna yönlendiriliyor
            // Şirkete dahil olmayan personeli EmployeeModal yerine direkt personel görev panosuna yönlendir
            navigate('/personnel/tasks');
            setLoading(false);
            return;
        }

        try {
            console.log(`[Login.tsx] Personel ${user.email} (UID: ${user.uid}) yetki kontrolü başlatılıyor`);
            console.log(`[Login.tsx] CompanyId: ${companyId}, User UID: ${user.uid}`);

            // WMS yetkisi varsa personeli WMS akisina yonlendir:
            // 1 ekran -> direkt, 2+ ekran -> secim sayfasi.
            const redirectedToWms = await tryNavigateToWmsScreens(companyId, user.uid);
            if (redirectedToWms) {
                setLoading(false);
                return;
            }
            
            // Faz 3: quickSaleScreens yetkisi SQL'den (HybridFallback RTDB).
            const activeScreenIds = await getActiveQuickSaleScreenIds(companyId, user.uid);
            console.log('[Login.tsx] Aktif ekran ID\'leri:', activeScreenIds);
            if (activeScreenIds.length > 0) {
                    const firstScreenId = activeScreenIds[0];
                    console.log(`[Login.tsx] İlk ekran ID: ${firstScreenId}`);
                    
                    // ✅ YENİ: Ekranın ScreenType'ını kontrol et (Restaurant mi, Standalone mi?)
                    try {
                        const screenResponse = await salesApi.get(`/api/QuickSaleScreen/${firstScreenId}`);
                        const screenData = screenResponse.data;
                        console.log('[Login.tsx] 📱 PERSONEL - API\'den gelen ekran verisi:', JSON.stringify(screenData, null, 2));
                        
                        const screenType = screenData.ScreenType || screenData.screenType || screenData.screentype || 'Standalone';
                        console.log(`[Login.tsx] 📱 PERSONEL - Tespit edilen ScreenType: ${screenType}`);
                        
                        if (screenType.toLowerCase() === 'restaurant') {
                            const restaurantScreenId = screenData.RestaurantScreenId || 
                                                       screenData.restaurantScreenId || 
                                                       screenData.RestaurantScreenID || 
                                                       screenData.restaurantscreenid;
                            
                            console.log(`[Login.tsx] 📱 PERSONEL - RestaurantScreenId (tüm varyantlar kontrol edildi):`, {
                                RestaurantScreenId: screenData.RestaurantScreenId,
                                restaurantScreenId: screenData.restaurantScreenId,
                                RestaurantScreenID: screenData.RestaurantScreenID,
                                restaurantscreenid: screenData.restaurantscreenid,
                                finalValue: restaurantScreenId
                            });
                            
                            if (restaurantScreenId) {
                                console.log(`[Login.tsx] 📱 ✅ PERSONEL restoran ekranına yönlendiriliyor: /restaurant-screen/${restaurantScreenId}`);
                                navigate(`/restaurant-screen/${restaurantScreenId}`);
                            } else {
                                console.error('[Login.tsx] 📱 ❌ RestaurantScreenId bulunamadı! API yanıtı:', screenData);
                                console.warn('[Login.tsx] 📱 ⚠️ Fallback: hızlı satış ekranına yönlendiriliyor');
                                navigate(`/hizli-satis/ekran/${firstScreenId}`);
                            }
                        } else if (screenType.toLowerCase() === 'package') {
                            console.log(`[Login.tsx] 📦 ✅ PERSONEL paket sipariş ekranına yönlendiriliyor: /paket-ekrani/${firstScreenId}`);
                            navigate(`/paket-ekrani/${firstScreenId}`);
                        } else {
                            console.log(`[Login.tsx] 📱 ✅ PERSONEL hızlı satış ekranına yönlendiriliyor: /hizli-satis/ekran/${firstScreenId}`);
                            navigate(`/hizli-satis/ekran/${firstScreenId}`);
                        }
                    } catch (apiError) {
                        console.error('[Login.tsx] ❌ API çağrısı hatası:', apiError);
                        // Hata durumunda varsayılan olarak hızlı satışa yönlendir
                        console.log(`[Login.tsx] Hata nedeniyle varsayılan hızlı satışa yönlendiriliyor: /hizli-satis/ekran/${firstScreenId}`);
                        navigate(`/hizli-satis/ekran/${firstScreenId}`);
                    }
                    
                    return;
                }
            
            // Faz 4: mutfak yetkisi SQL'den (HybridFallback RTDB).
            {
                const permittedKitchenScreens = await getRedirectPermMap(companyId, user.uid, 'kitchenScreens');
                for (const menuId of Object.keys(permittedKitchenScreens)) {
                    const permissions = permittedKitchenScreens[menuId];
                    if (permissions?.accessKitchenScreen === true) {
                        console.log(`[Login.tsx] Personel mutfak ekranına yönlendiriliyor: ${menuId}`);
                        navigate(`/mutfak-ekrani/${menuId}`, { state: { fromLogin: true } });
                        return;
                    }
                }
            }
            
            // Faz 4: musteri ekrani (Pole Display) yetkisi SQL'den (HybridFallback RTDB).
            {
                const permittedPoleDisplayScreens = await getRedirectPermMap(companyId, user.uid, 'poleDisplayScreens');
                for (const menuId of Object.keys(permittedPoleDisplayScreens)) {
                    const permissions = permittedPoleDisplayScreens[menuId];
                    if (permissions?.accessPoleDisplay === true && permissions?.autoRedirect === true) {
                        console.log(`[Login.tsx] Personel müşteri ekranına yönlendiriliyor: ${menuId}`);
                        navigate(`/musteri-ekrani/${menuId}`, { state: { fromLogin: true } });
                        return;
                    }
                }
            }
            
            // SQL'deki PYK ekran ataması kontrolü (yeni sistem — atama SQL'de tutuluyor)
            if (await tryNavigateToSqlPykScreen(user.uid)) {
                return;
            }

            // Hiçbir özel yetki yoksa görev panosuna yönlendir
            console.log(`[Login.tsx] Personel özel yetkisi yok, görev panosuna yönlendiriliyor`);
            navigate('/personnel/tasks');
        } catch (permissionError) {
            console.error("Personel yetkileri kontrol edilirken hata:", permissionError);
            await logout();
            setError("Yetki kontrolü sırasında bir hata oluştu. Tekrar deneyin.");
            setLoading(false);
        }

      } else {
        // Tanımsız veya beklenmeyen rol
        console.log(`Beklenmeyen kullanıcı rolü: ${role}. Web erişimi engellendi.`);
        await logout();
        setError(t('loginPage.internalErrors.unexpectedRoleError'));
        setLoading(false);
        // İsteğe bağlı olarak burada da EmployeeModal gösterilebilir
        // setShowEmployeeModal(true);
      }
      
      // ✅ YENİ: Loading'i kapat (tüm yönlendirmeler için fallback)
      console.log(`🔵 [Login][${submitId}] step:5 fallback setLoading(false) — hiçbir role match etmedi`);
      setLoading(false);
    } catch (err: any) {
      console.error(`❌ [Login][${submitId}] CATCH — submit başarısız oldu:`, {
        message: err?.message,
        code: err?.code,
        name: err?.name,
        stack: err?.stack?.split('\n').slice(0, 4).join('\n')
      });
      
      // Hatalı giriş sayacını artır
      const newAttemptCount = failedLoginAttempts + 1;
      setFailedLoginAttempts(newAttemptCount);
      
      // LocalStorage'e kaydet
      localStorage.setItem('failedLoginAttempts', newAttemptCount.toString());
      localStorage.setItem('lastFailedLoginTimestamp', new Date().getTime().toString());
      
      // 3 veya daha fazla başarısız deneme varsa CAPTCHA göster
      if (newAttemptCount >= 3) {
        setShowCaptcha(true);
        // CAPTCHA'yı sıfırla
        recaptchaRef.current?.reset();
        setCaptchaVerified(false);
      }
      
      let errorMessage = t('loginPage.genericError');
      
      if (err.code) {
        switch (err.code) {
          case 'auth/user-not-found':
            errorMessage = t('loginPage.genericError');
            break;
          case 'auth/wrong-password':
            errorMessage = t('loginPage.genericError');
            break;
          case 'auth/invalid-email':
            errorMessage = t('loginPage.invalidEmailError');
            break;
          case 'auth/invalid-credential':
            errorMessage = t('loginPage.genericError');
            break;
          case 'auth/user-disabled':
            errorMessage = t('loginPage.userDisabledError');
            break;
          case 'auth/too-many-requests':
            errorMessage = t('loginPage.tooManyRequestsError');
            break;
          default:
            errorMessage = t('loginPage.genericError');
            break;
        }
      }
      
      setError(errorMessage);
      setLoading(false);
    }
  };

  // Rol seçim modalını aç (Normal kayıt — global popup akışı)
  const handleOpenRoleModal = () => {
    setIsGoogleAuthFlow(false);
    openRoleSelection();
  };

  // Görev resmine tıklama
  const handleTaskImageClick = () => {
    setImageMessage(t('loginPage.taskImageMessage'));
    setShowImageMessage(true);
  };

  // Muhasebe resmine tıklama
  const handleAccountingImageClick = () => {
    setImageMessage(t('loginPage.accountingImageMessage'));
    setShowImageMessage(true);
  };

  // Bilgi mesajını kapat
  const handleCloseImageMessage = () => {
    setShowImageMessage(false);
  };

  // PYK izinleri kontrolü için yardımcı fonksiyon
  const checkPykPermissions = async (userId: string, companyId: string) => {
    try {
      // SQL'deki PYK ekran ataması kontrolü (yeni sistem — atama SQL'de tutuluyor)
      if (await tryNavigateToSqlPykScreen(userId)) {
        return;
      }

      console.log("PYK ekranı yetkisi bulunamadı, personel görev panosuna yönlendiriliyor");
      // Personel web desteği aktivasyonu - görev panosuna yönlendir
      navigate('/personnel/tasks');
    } catch (error) {
      console.error("PYK izinleri kontrol edilirken hata:", error);
      await logout();
      setError(t('loginPage.internalErrors.permissionError'));
    }
  };
  
  // Google ile giriş — own-auth (gts_auth_api).
  // Firebase yalnızca Google ID token KAYNAĞI olarak kullanılır: context'teki
  // googleLoginPopup signInWithPopup ile token alır, hemen firebaseSignOut yapar
  // ve token'ı own /login-google endpoint'inde değişir. Firebase oturumu TUTULMAZ.
  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');

    try {
      const user = await googleLoginPopup();
      if (!user) {
        throw new Error('Giriş başarılı oldu ancak kullanıcı bilgileri alınamadı.');
      }

      // Kullanıcı verilerini SQL'den al (role bazlı yönlendirme için).
      let userData: any = null;
      try {
        userData = await firebaseDataService.getUserData(user.uid);
      } catch (cloudError) {
        console.warn('⚠️ [Login-Google] getUserData hatası, cache\'siz tekrar denenecek:', cloudError);
      }
      if (!userData) {
        userData = await firebaseDataService.getUserData(user.uid, false);
      }
      if (!userData) {
        await logout();
        throw new Error('Kullanıcı profili bulunamadı. Lütfen sistem yöneticisi ile iletişime geçin.');
      }

      const role = userData.role || 'employee';

      // Rolüne göre yönlendir (handleSubmit'teki gibi)
      if (role === 'superadmin' || role === 'dealer') {
        navigate(getPanelHomePath(role));
      } else if (role === 'admin') {
        navigate('/accounting/dashboard');
      } else if (role === 'manager') {
        navigate('/manager');
      } else if (role === 'employee') {
        navigate('/personnel/tasks');
      } else {
        await logout();
        throw new Error('Geçersiz kullanıcı rolü.');
      }

      setGoogleLoading(false);
    } catch (err: any) {
      console.error('Google ile giriş hatası:', err);
      let errorMessage = 'Google ile giriş sırasında bir hata oluştu.';
      if (err?.code === 'auth/popup-closed-by-user' || err?.code === 'auth/cancelled-popup-request') {
        errorMessage = 'Google ile giriş iptal edildi.';
      } else if (err?.message) {
        errorMessage = err.message;
      }
      setError(errorMessage);
      setGoogleLoading(false);
    }
  };

  // Google ile giriş başlatma fonksiyonu (Rol seçildikten sonra çağrılacak)
  const startGoogleOneTapSignIn = () => {
    if (window.google && window.google.accounts && googleInitialized) {
      console.log("[startGoogleOneTapSignIn] Google One Tap prompt gösteriliyor.");
      setGoogleLoading(true); 
      setError('');

      window.google.accounts.id.prompt((notification: PromptMomentNotification) => {
        if (notification.isNotDisplayed()) {
          const notDisplayedReason = notification.getNotDisplayedReason();
          console.error("[!!! IMPORTANT !!!] Google One Tap GÖSTERİLEMEDİ, Sebep:", notDisplayedReason);
          setError(`Google ile giriş arayüzü gösterilemedi. Sebep: ${notDisplayedReason}. Lütfen tarayıcı ayarlarınızı kontrol edin veya daha sonra tekrar deneyin.`);
          setGoogleLoading(false);
          setIsGoogleAuthFlow(false); 
        } else if (notification.isSkippedMoment()) {
          const skippedReason = notification.getSkippedReason();
          console.warn("[Google One Tap] Kullanıcı tarafından ATLANDI, Sebep:", skippedReason);
          // setError(`Google ile giriş kullanıcı tarafından atlandı. Sebep: ${skippedReason}`); // Kullanıcıya hata olarak göstermek isteğe bağlı
          setGoogleLoading(false);
          setIsGoogleAuthFlow(false); 
        } else if (notification.isDismissedMoment()) {
          const dismissedReason = notification.getDismissedReason();
          console.warn("[Google One Tap] Kullanıcı tarafından KAPATILDI, Sebep:", dismissedReason);
          // setError(`Google ile giriş kullanıcı tarafından kapatıldı. Sebep: ${dismissedReason}`); // Kullanıcıya hata olarak göstermek isteğe bağlı
          setGoogleLoading(false);
          setIsGoogleAuthFlow(false); 
        } else {
          // Prompt başarıyla gösterildi, credential callback (handleGoogleOneTapResponse) bekleniyor.
          // Eğer bir credential gelmezse, bu genellikle kullanıcının prompt'u bir seçim yapmadan kapattığı anlamına gelir.
          console.log("[startGoogleOneTapSignIn] Google One Tap prompt başarıyla gösterildi veya farklı bir durum oluştu. Callback bekleniyor.");
        }
        // Başarılı giriş `handleGoogleOneTapResponse` callback'i üzerinden işlenecek.
        // setGoogleLoading(false) burada çağrılmamalı, handleGoogleOneTapResponse'un finally bloğunda yapılmalı.
      });
    } else {
      console.error("[startGoogleOneTapSignIn] Google API yüklenmedi veya One Tap başlatılmadı.");
      setError("Google ile giriş şu anda kullanılamıyor. Lütfen sayfayı yenileyin.");
      setGoogleLoading(false);
      setIsGoogleAuthFlow(false); // Akışı sıfırla
    }
  };

  return (
    <PageContainer>
      <Header />
      <LoginContent>
        {/* Google One Tap için gerekli DOM öğesi - DEVRE DIŞI BIRAKILDI */}
        {/* <div id="g_id_onload"
          data-client_id="338071169045-4kj277asrv67kp1u8mtn84p4gvo3pubh.apps.googleusercontent.com"
          data-context="signin"
          data-ux_mode="popup"
          data-auto_select="false"
          data-itp_support="true"
          data-use_fedcm_for_prompt="true">
        </div> */}
        
        <LoginSection>
          {/* Sol Görev Görseli */}
          <ImageContainer>
            <Image 
              src={BRAND.logos.login}
              alt={BRAND.fullName}
              onClick={handleTaskImageClick}
              sx={{ cursor: 'pointer' }}
            />
          </ImageContainer>
          
          {/* Login Formu */}
          <FormContainer>
            <LoginForm elevation={3}>
              <Logo src={BRAND.logos.loginForm} alt={`${BRAND.name} Logo`} />
              
              <Box component="form" onSubmit={handleSubmit} width="100%" ref={loginFormRef}>
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  id="email"
                  label={t('loginPage.emailLabel')}
                  name="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  name="password"
                  label={t('loginPage.passwordLabel')}
                  type="password"
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
                
                {/* Beni Hatırla Checkbox - GİZLENDİ (her zaman aktif) */}
                {/* <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'flex-start' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={rememberMe}
                        onChange={(e) => setRememberMe(e.target.checked)}
                        name="rememberMe"
                        color="primary"
                        size="small"
                      />
                    }
                    label={
                      <Typography variant="body2" sx={{ fontSize: '14px', color: 'text.secondary' }}>
                        {t('loginPage.rememberMe')}
                      </Typography>
                    }
                    sx={{ 
                      margin: 0,
                      '& .MuiFormControlLabel-label': {
                        fontSize: '14px'
                      }
                    }}
                  />
                </Box> */}
                
                {/* CAPTCHA Bileşeni */}
                {showCaptcha && !isDevelopment && (
                  <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                    <ReCAPTCHA
                      ref={recaptchaRef}
                      // Env'den oku (USA: .env.usa). Set degilse TR'nin orijinal hardcoded key'ini kullan.
                      sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LfyLj4rAAAAAG9wC_MkM5rwiIJMMsGS8GK0fYgN"}
                      onChange={handleCaptchaVerify}
                      onExpired={handleCaptchaExpired}
                      hl={process.env.REACT_APP_RECAPTCHA_LANG || "tr"}
                    />
                  </Box>
                )}
                
                {/* Localhost'ta CAPTCHA Bilgisi */}
                {showCaptcha && isDevelopment && (
                  <Box sx={{ mt: 2, mb: 2, p: 2, textAlign: 'center', bgcolor: '#e3f2fd', borderRadius: 2 }}>
                    <Typography color="primary" fontWeight="medium">
                      {t('loginPage.devCaptchaInfo')}
                    </Typography>
                  </Box>
                )}
                
                {/* Giriş butonu tam genişlikte; Google açılırsa ikisi eşit paylaşır */}
                <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                  {/* Giriş Yap Butonu */}
                  <Button
                    type="submit"
                    variant="outlined"
                    disabled={loading || (showCaptcha && !captchaVerified) || googleRedirectProcessing}
                    size="large"
                    sx={{ 
                      flex: 1, 
                      py: 1.6,
                      minWidth: 'auto',
                      borderRadius: 3,
                      fontWeight: 'bold',
                      fontSize: 17,
                      borderColor: '#dadce0',
                      color: '#0D47A1',
                      '&:hover': {
                        borderColor: '#0D47A1',
                        backgroundColor: 'rgba(13, 71, 161, 0.04)',
                      }
                    }}
                  >
                    {loading ? <CircularProgress size={20} style={{ color: '#0D47A1' }} /> : t('loginPage.loginButton')}
                  </Button>

                  {/* Google İkonu Butonu - geçici olarak gizli (bkz. config/featureFlags.ts) */}
                  {GOOGLE_LOGIN_ENABLED && (
                  <Button
                    variant="outlined"
                    disabled={googleLoading || loading}
                    onClick={handleGoogleSignIn}
                    sx={{ 
                      flex: 1, 
                      py: 1.5,
                      minWidth: 'auto',
                      borderRadius: 3,
                      borderColor: '#dadce0',
                      '&:hover': {
                        borderColor: '#4285F4',
                        backgroundColor: 'rgba(66, 133, 244, 0.04)',
                      }
                    }}
                  >
                    {googleLoading ? (
                      <CircularProgress size={24} style={{ color: '#4285F4' }} />
                    ) : (
                      <GoogleIcon style={{ color: '#4285F4', fontSize: 32 }} />
                    )}
                  </Button>
                  )}

                </Box>

                {/* Şifremi Unuttum Butonu */}
                <Button
                  onClick={handleForgotPasswordClick}
                  sx={{
                    mt: 2,
                    color: '#0D47A1',
                    textTransform: 'none',
                    fontWeight: 'bold',
                    fontSize: 14,
                    display: 'block',
                    width: '100%',
                    textAlign: 'center',
                    '&:hover': {
                      backgroundColor: 'transparent',
                      textDecoration: 'underline',
                    }
                  }}
                >
                  {t('loginPage.forgotPasswordButton')}
                </Button>

                {/* Kayıt olma ikincil bir eylem: butonla giriş butonuyla aynı
                    ağırlıkta görünüyordu, en altta küçük bir bağlantıya alındı. */}
                <Typography
                  variant="caption"
                  component="div"
                  sx={{ mt: 1.5, textAlign: 'center', color: 'text.secondary' }}
                >
                  <Box
                    component="span"
                    role="button"
                    tabIndex={0}
                    onClick={handleOpenRoleModal}
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === 'Enter' || e.key === ' ') handleOpenRoleModal();
                    }}
                    sx={{
                      color: '#0D47A1',
                      cursor: 'pointer',
                      fontWeight: 600,
                      '&:hover': { textDecoration: 'underline' },
                    }}
                  >
                    {t('loginPage.registerButton')}
                  </Box>
                </Typography>
              </Box>
              
              <Snackbar 
                open={!!error} 
                autoHideDuration={6000} 
                onClose={() => setError('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
              >
                <Alert 
                  onClose={() => setError('')} 
                  severity="error"
                  sx={{
                    fontWeight: 'bold',
                    fontSize: '16px',
                    width: '100%'
                  }}
                >
                  {error}
                </Alert>
              </Snackbar>
            </LoginForm>
          </FormContainer>
          
          {/* Sağ Muhasebe Görseli */}
          <ImageContainer>
            <Image 
              src={BRAND.logos.loginAccounting}
              alt="Muhasebe" 
              onClick={handleAccountingImageClick}
              sx={{ cursor: 'pointer' }}
            />
          </ImageContainer>
        </LoginSection>

        {/* Personel Modalı */}
        <EmployeeModal 
          open={showEmployeeModal} 
          onClose={handleCloseEmployeeModal} 
        />
        
        {/* Şifre Sıfırlama Modalı */}
        <Dialog 
          open={showResetModal} 
          onClose={handleCloseResetModal}
          PaperProps={{
            sx: {
              borderRadius: 3,
              width: '100%',
              maxWidth: 400,
              px: 1
            }
          }}
        >
          <DialogTitle sx={{ 
            textAlign: 'center',
            fontWeight: 'bold',
            color: '#0D47A1',
            pt: 3
          }}>
            {t('loginPage.resetPassword.title')}
          </DialogTitle>
          
          <DialogContent>
            <Typography 
              variant="body2" 
              sx={{ 
                mb: 2,
                textAlign: 'center',
                color: 'text.secondary'
              }}
            >
              {t('loginPage.resetPassword.instruction')}
            </Typography>
            
            <TextField
              autoFocus
              fullWidth
              variant="outlined"
              label={t('loginPage.emailLabel')}
              value={resetEmail}
              onChange={(e) => setResetEmail(e.target.value)}
              disabled={resetLoading}
              sx={{ mb: 2 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            
            {resetError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {resetError}
              </Alert>
            )}
            
            {resetSuccess && (
              <Alert severity="success" sx={{ mb: 2 }}>
                {resetSuccess}
              </Alert>
            )}
          </DialogContent>
          
          <DialogActions sx={{ pb: 3, px: 3, justifyContent: 'space-between' }}>
            <Button 
              onClick={handleCloseResetModal}
              disabled={resetLoading}
              sx={{ color: 'text.secondary' }}
            >
              {t('loginPage.resetPassword.cancelButton')}
            </Button>
            
            <Button
              variant="contained"
              onClick={handlePasswordReset}
              disabled={resetLoading}
              sx={{
                bgcolor: '#0D47A1',
                '&:hover': {
                  bgcolor: '#0A3C87',
                },
                minWidth: 100
              }}
            >
              {resetLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                t('loginPage.resetPassword.sendButton')
              )}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Google Rol Seçim Modalı (Google ile Giriş/Kayıt için) */}
        <Modal
          open={showGoogleRoleSelectionModal}
          onClose={() => { // Bu modalın onClose'u doğru görünüyor
            setShowGoogleRoleSelectionModal(false);
            setIsGoogleAuthFlow(false); 
            setRoleForGoogleSignIn(null); 
          }}
          aria-labelledby="google-role-selection-title"
        >
          <RoleSelectionModalContainer>
            <Card elevation={5} sx={{ width: '100%', overflow: 'hidden' }}>
              <Box sx={{ 
                py: 3, 
                px: 3, 
                textAlign: 'center',
                background: 'linear-gradient(to right, #0D47A1, #1976D2)',
                color: 'white'
              }}>
                <Typography 
                  id="google-role-selection-title" 
                  variant="h5" 
                  component="h2"
                  fontWeight="bold"
                >
                  {t('loginPage.googleRoleSelection.title')}
                </Typography>
              </Box>
              
              <CardContent sx={{ p: 4 }}>
                <Typography 
                  variant="body1" 
                  align="center" 
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t('loginPage.googleRoleSelection.instruction')}
                </Typography>
                
                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2 }}>
                  {/* Yönetici Olarak Google ile Devam Et Butonu */}
                  <RoleButton 
                    onClick={() => {
                      console.log("Google için Yönetici rolü seçildi. Şirket/KVKK modalı açılacak.");
                      setRoleForGoogleSignIn('admin');
                      setShowGoogleRoleSelectionModal(false); // Önceki modalı kapat
                      setGoogleAdminCompanyName(''); // Şirket adını sıfırla
                      setGoogleAdminKvkkAccepted(false); // KVKK onayını sıfırla
                      setShowCompanyKvkkModal(true);    // Yeni Şirket/KVKK modalını aç
                      // startGoogleOneTapSignIn(); // One Tap burada ÇAĞRILMAYACAK
                    }}
                    disabled={googleLoading} 
                  >
                    {googleLoading && roleForGoogleSignIn === 'admin' ? (
                      <CircularProgress size={24} color="primary" /> 
                    ) : (
                      <>
                        <RoleIcon>
                          <AdminPanelSettingsIcon fontSize="large" sx={{ fontSize: 48 }} />
                        </RoleIcon>
                        <RoleTitle>
                          {t('loginPage.googleRoleSelection.adminRole')}
                        </RoleTitle>
                        <RoleSubtitle sx={{ height: 'auto', lineHeight: 1.2, paddingTop: 0, marginTop: 0 }}>
                          {t('loginPage.googleRoleSelection.adminDescription')}
                        </RoleSubtitle>
                      </>
                    )}
                  </RoleButton>
                  
                  {/* Personel Olarak Google ile Devam Et Butonu */}
                  <RoleButton 
                    onClick={() => {
                      console.log("Google için Personel rolü seçildi.");
                      setRoleForGoogleSignIn('employee');
                      setShowGoogleRoleSelectionModal(false);
                      startGoogleOneTapSignIn(); // One Tap'i tetikle
                    }}
                    disabled={googleLoading} // googleLoading veya kendi loading state'i olabilir
                  >
                    {googleLoading && roleForGoogleSignIn === 'employee' ? (
                      <CircularProgress size={24} color="primary" /> 
                    ) : (
                      <>
                        <RoleIcon>
                          <PersonOutlineIcon fontSize="large" sx={{ fontSize: 48 }} />
                        </RoleIcon>
                        <RoleTitle>
                          {t('loginPage.googleRoleSelection.personnelRole')}
                        </RoleTitle>
                        <RoleSubtitle>
                          {t('loginPage.googleRoleSelection.personnelDescription')}
                        </RoleSubtitle>
                      </>
                    )}
                  </RoleButton>
                </Box>
                
                <Box sx={{ mt: 4, textAlign: 'center' }}>
                  <Button 
                    onClick={() => {
                      setShowGoogleRoleSelectionModal(false);
                      setIsGoogleAuthFlow(false); // Google akışını iptal et
                      setRoleForGoogleSignIn(null); // Seçilen rolü sıfırla
                    }}
                    disabled={googleLoading}
                    sx={{ color: 'text.secondary' }}
                  >
                    {t('loginPage.googleRoleSelection.cancelButton')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </RoleSelectionModalContainer>
        </Modal>

        {/* Google Admin için Şirket Adı ve KVKK Onay Modalı */}
        <Modal
          open={showCompanyKvkkModal}
          onClose={() => { // Bu modalın onClose'u da doğru görünüyor
            setShowCompanyKvkkModal(false);
            if (isGoogleAuthFlow && roleForGoogleSignIn === 'admin') {
              setIsGoogleAuthFlow(false);
              setRoleForGoogleSignIn(null);
            }
          }}
          aria-labelledby="company-kvkk-selection-title"
        >
          <RoleSelectionModalContainer sx={{ maxWidth: 550 }}> {/* Biraz daha geniş olabilir */}
            <Card elevation={5} sx={{ width: '100%', overflow: 'hidden' }}>
              <Box sx={{ 
                py: 3, 
                px: 3, 
                textAlign: 'center',
                background: 'linear-gradient(to right, #0D47A1, #1976D2)',
                color: 'white'
              }}>
                <Typography 
                  id="company-kvkk-selection-title" 
                  variant="h5" 
                  component="h2"
                  fontWeight="bold"
                >
                  {t('loginPage.companyKvkkModal.title')}
                </Typography>
              </Box>
              
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4} }}>
                <Typography 
                  variant="body1" 
                  align="center" 
                  color="text.secondary"
                  sx={{ mb: 3 }}
                >
                  {t('loginPage.companyKvkkModal.instruction')}
                </Typography>
                
                <StyledTextField
                  variant="outlined"
                  fullWidth
                  id="googleAdminCompanyName"
                  label={t('loginPage.companyKvkkModal.companyNameLabel')}
                  name="googleAdminCompanyName"
                  value={googleAdminCompanyName}
                  onChange={(e) => setGoogleAdminCompanyName(e.target.value)}
                  required
                  sx={{ mb: 2 }} // Alt boşluk
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />

                <FormControlLabel
                  control={ (
                    <Checkbox
                      checked={googleAdminKvkkAccepted}
                      onChange={(e) => setGoogleAdminKvkkAccepted(e.target.checked)}
                      name="googleAdminKvkkAccepted"
                      color="primary"
                    />
                  )}
                  label={ (
                    <Typography variant="body2" sx={{ fontSize: '0.9rem' }}>
                      <Link 
                        href="#" 
                        onClick={(e) => { 
                          e.preventDefault(); 
                          setIsKvkkModalOpen(true);
                        }}
                        underline="hover"
                      >
                        {t('loginPage.companyKvkkModal.kvkkLink')}
                      </Link>
                      {' '}
                      {t('loginPage.companyKvkkModal.kvkkAgreement')}
                    </Typography>
                  )}
                  sx={{ mb: 2, mt: 1, display: 'flex', justifyContent: 'center'}}
                />
                
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, mt: 2 }}>
                  <Button 
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => {
                      if (!googleAdminCompanyName.trim()) {
                        setError(t('loginPage.companyKvkkModal.companyNameRequiredError'));
                        return;
                      }
                      if (!googleAdminKvkkAccepted) {
                        setError(t('loginPage.companyKvkkModal.kvkkRequiredError'));
                        return;
                      }
                      console.log("Şirket adı ve KVKK onayı alındı. Google One Tap tetiklenecek.");
                      setShowCompanyKvkkModal(false);
                      startGoogleOneTapSignIn(); // One Tap'i tetikle
                    }}
                    disabled={googleLoading || !googleAdminCompanyName.trim() || !googleAdminKvkkAccepted}
                    sx={{ py: 1.5, fontSize: '1rem', borderRadius: '12px' }}
                  >
                    {googleLoading ? <CircularProgress size={24} color="inherit" /> : t('loginPage.companyKvkkModal.submitButton')}
                  </Button>
                  
                  <Button 
                    onClick={() => {
                      setShowCompanyKvkkModal(false);
                      // Google akışından çıkılıyorsa diğer state'leri de sıfırla
                      setIsGoogleAuthFlow(false);
                      setRoleForGoogleSignIn(null);
                    }}
                    sx={{ color: 'text.secondary' }}
                    fullWidth
                  >
                    {t('loginPage.companyKvkkModal.cancelButton')}
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </RoleSelectionModalContainer>
        </Modal>

        <KvkkModal open={isKvkkModalOpen} onClose={() => setIsKvkkModalOpen(false)} />

        {/* Resim Bilgi Mesajı */}
        <Snackbar 
          open={showImageMessage} 
          autoHideDuration={6000} 
          onClose={handleCloseImageMessage}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert 
            onClose={handleCloseImageMessage} 
            severity="info"
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%',
              backgroundColor: '#1976d2',
              color: 'white'
            }}
          >
            {imageMessage}
          </Alert>
        </Snackbar>

        {/* Çok Yakında Mesajı */}
        <Snackbar 
          open={showComingSoonMessage} 
          autoHideDuration={3000} 
          onClose={() => setShowComingSoonMessage(false)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert 
            onClose={() => setShowComingSoonMessage(false)} 
            severity="info"
            sx={{
              fontWeight: 'bold',
              fontSize: '16px',
              width: '100%'
            }}
          >
            {t('loginPage.googleComingSoon')}
          </Alert>
        </Snackbar>

        <Snackbar
          open={showAutoLoginNotice}
          autoHideDuration={2800}
          onClose={() => setShowAutoLoginNotice(false)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert
            onClose={() => setShowAutoLoginNotice(false)}
            severity="info"
            sx={{ width: '100%', fontWeight: 600 }}
          >
            Giriş yapılıyor...
          </Alert>
        </Snackbar>
      </LoginContent>
      <Footer />
      <WhatsAppFloatingButton />
    </PageContainer>
  );
};

export default Login;